var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { TSL_DIAGNOSTIC_KEYS } from '../tsl/core/parser';
import { expandField, parseFieldAddress } from './address';
import { FIELD_QUERY_RELATIONS, TYPE_PSEUDO_KEY } from './registry';
/** Diagnostic keys the compiler adds to the parser's own. */
export var FIELD_QUERY_DIAGNOSTIC_KEYS = {
    unknownField: 'fieldQuery.diagnostic.unknownField',
    nestedWords: 'fieldQuery.diagnostic.nestedWords',
    invalidRegex: 'fieldQuery.diagnostic.invalidRegex',
    tooManyConditions: 'fieldQuery.diagnostic.tooManyConditions',
    tooDeep: 'fieldQuery.diagnostic.tooDeep',
    valueTooLong: 'fieldQuery.diagnostic.valueTooLong',
};
/** The backend's limits (`FieldQueryParser`), checked here so they are marked in the line. */
export var MAX_CONDITIONS = 20;
export var MAX_DEPTH = 8;
export var MAX_VALUE_LENGTH = 500;
var RELATION_BY_CODE = new Map(FIELD_QUERY_RELATIONS.map(function (entry) { return [entry.code, entry.relation]; }));
var error = function (state, messageKey, span, params) {
    state.diagnostics.push({ severity: 'error', messageKey: messageKey, span: span, params: params });
};
/** Span of the field address at the start of a clause. */
var fieldSpan = function (clause, address) { return ({
    start: clause.span.start,
    end: clause.span.start + address.length,
}); };
var compileCondition = function (state, clause) {
    var _a, _b;
    var key = (_a = clause.fieldKey) !== null && _a !== void 0 ? _a : '';
    var address = parseFieldAddress(key);
    var relation = RELATION_BY_CODE.get(clause.relation);
    // Every relation the registry offers maps; a missing one would be a registry bug.
    if (!address || !relation || ((_b = expandField(address.field, state.catalog)) === null || _b === void 0 ? void 0 : _b.length) === 0) {
        error(state, FIELD_QUERY_DIAGNOSTIC_KEYS.unknownField, fieldSpan(clause, key), { field: key });
        return undefined;
    }
    if (clause.value !== undefined && clause.value.length > MAX_VALUE_LENGTH) {
        error(state, FIELD_QUERY_DIAGNOSTIC_KEYS.valueTooLong, clause.span, {
            max: String(MAX_VALUE_LENGTH),
        });
    }
    if (relation === 'MATCHES' && clause.value !== undefined) {
        // A pre-check only: the backend's own check (Postgres) is the authority. JavaScript
        // rejects a few patterns Postgres accepts, so this warns instead of blocking.
        try {
            RegExp(clause.value);
        }
        catch (_c) {
            state.diagnostics.push({
                severity: 'warning',
                messageKey: FIELD_QUERY_DIAGNOSTIC_KEYS.invalidRegex,
                span: clause.span,
                params: { value: clause.value },
            });
        }
    }
    state.conditions += 1;
    var condition = __assign(__assign(__assign(__assign({ field: address.field }, (address.subfield ? { subfield: address.subfield } : {})), { relation: relation }), (relation === 'EXISTS' ? {} : { value: clause.value })), (state.matchCase && relation !== 'EXISTS' ? { matchCase: true } : {}));
    return clause.negated ? { op: 'not', child: condition } : condition;
};
var compilePseudo = function (state, clause) {
    if (clause.pseudoKey !== TYPE_PSEUDO_KEY) {
        return undefined;
    }
    state.conditions += 1;
    var condition = { attribute: 'type', relation: 'EQUALS', value: clause.valueId };
    return clause.negated ? { op: 'not', child: condition } : condition;
};
var treeDepth = function (node) {
    if ('op' in node) {
        var children = node.op === 'not' ? [node.child] : node.children;
        return 1 + Math.max.apply(Math, children.map(treeDepth));
    }
    return 1;
};
var firstSpan = function (node) {
    return node.type === 'clause' ? node.clause.span : node.children.map(firstSpan).find(Boolean);
};
var compileNode = function (state, node) {
    if (node.type === 'clause') {
        var clause = node.clause;
        if (clause.kind === 'fulltext') {
            // Bare words are the heading search, which is one filter for the whole query —
            // only an AND at the top level can say what they mean.
            error(state, FIELD_QUERY_DIAGNOSTIC_KEYS.nestedWords, clause.span, { value: clause.text });
            return undefined;
        }
        return clause.kind === 'condition' ? compileCondition(state, clause) : compilePseudo(state, clause);
    }
    var children = node.children
        .map(function (child) { return compileNode(state, child); })
        .filter(function (child) { return child !== undefined; });
    if (children.length === 0) {
        return undefined;
    }
    return children.length === 1 ? children[0] : { op: node.type, children: children };
};
/**
 * Compiles a parsed query line into the backend's condition tree plus the bare words.
 * Never throws; everything wrong is a positioned diagnostic, and `hasErrors` says whether
 * the line may run. `matchCase` (CZNA-1400, the checkbox under the line) marks every
 * condition that has a value; bare words stay the heading search and are not affected.
 */
export var compileFieldQuery = function (query, catalog, matchCase) {
    var _a;
    if (matchCase === void 0) { matchCase = false; }
    // An unknown record type is only a warning to the parser, which then drops the clause —
    // running the line would silently search without it, so here it blocks.
    var diagnostics = query.diagnostics.map(function (diagnostic) { return (diagnostic.messageKey === TSL_DIAGNOSTIC_KEYS.unknownPseudoValue
        ? __assign(__assign({}, diagnostic), { severity: 'error' }) : diagnostic); });
    var state = { catalog: catalog, matchCase: matchCase, diagnostics: diagnostics, conditions: 0 };
    var words = [];
    var tree;
    if (query.tree) {
        // The top level is the only place bare words may stand: as the line itself, or as
        // direct children of a top-level AND.
        var top_1 = query.tree;
        var topChildren = top_1.type === 'and' ? top_1.children : [top_1];
        var conditions = [];
        for (var _i = 0, topChildren_1 = topChildren; _i < topChildren_1.length; _i++) {
            var child = topChildren_1[_i];
            if (child.type === 'clause' && child.clause.kind === 'fulltext') {
                words.push(child.clause.text);
            }
            else {
                var compiled = compileNode(state, child);
                if (compiled) {
                    conditions.push(compiled);
                }
            }
        }
        if (conditions.length === 1) {
            tree = conditions[0];
        }
        else if (conditions.length > 1) {
            tree = { op: 'and', children: conditions };
        }
    }
    // Measured on the tree that is SENT, counted the way the backend counts it: every node,
    // leaves and `not` included, from 1 at the root.
    if (tree && treeDepth(tree) > MAX_DEPTH) {
        error(state, FIELD_QUERY_DIAGNOSTIC_KEYS.tooDeep, (_a = (query.tree && firstSpan(query.tree))) !== null && _a !== void 0 ? _a : { start: 0, end: query.input.length }, {
            max: String(MAX_DEPTH),
        });
    }
    if (state.conditions > MAX_CONDITIONS) {
        error(state, FIELD_QUERY_DIAGNOSTIC_KEYS.tooManyConditions, { start: 0, end: query.input.length }, {
            max: String(MAX_CONDITIONS),
        });
    }
    return __assign(__assign(__assign({}, (tree ? { tree: tree } : {})), (words.length > 0 ? { words: words.join(' ') } : {})), { diagnostics: state.diagnostics, hasErrors: state.diagnostics.some(function (diagnostic) { return diagnostic.severity === 'error'; }) });
};
